import {
  Box,
  Button,
  Switch,
  useTheme,
  TextField,
  FormLabel,
  Typography,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
  FormHelperText,
  FormControlLabel,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import { Book } from 'pages/dashboard';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { EFFORT_STATUS_TYPE_ENUM } from 'enums';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isPastMonthOvertime, isPreviousMonthOvertime } from '../../utils';
import { Authentication, renderAutoCompleteLocaleText, renderDatePickerLocaleText } from 'utils';
import { Project, ActivityType, ActivityBoardItem, EffortItem, User, HolidayItem } from 'library/types';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

type UpdateActivityBoardItemFormParams = {
  activityTypeId: ActivityType | null;
  effortTrackNo: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  estimatedHours: string;
  description: string;
  isOvertimeWork: boolean;
};

export type UpdateActivityBoardItemPostFormParams = {
  id: number;
  bookId: number;
  projectId: number;
  activityTypeId: number;
  effortTrackNo: string;
  startDate: string | null;
  endDate: string | null;
  estimatedHours: number;
  description: string;
  isOvertimeWork: boolean;
};

const defaultValues: UpdateActivityBoardItemFormParams = {
  activityTypeId: null,
  effortTrackNo: '',
  startDate: null,
  endDate: null,
  estimatedHours: '',
  description: '',
  isOvertimeWork: false,
};

type UpdateActivityBoardItemDialogProps = {
  open: boolean;
  handleClose: () => void;
  selectedRow: ActivityBoardItem | null;
  books: Book[];
  products: Project[];
  activityTypes: ActivityType[];
  users?: User[];
  refresh: () => Promise<void>;
};

const UpdateActivityBoardItemDialog = ({
  open,
  handleClose: closeDialog,
  selectedRow,
  books,
  products,
  activityTypes,
  users = [],
  refresh,
}: UpdateActivityBoardItemDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [book, setBook] = useState<Book | null>(null);
  const [product, setProduct] = useState<Project | null>(null);
  const [efforts, setEfforts] = useState<EffortItem[]>([]);
  const [holidays, setHolidays] = useState<HolidayItem[]>([]);
  const [userPermissionActivities, setUserPermissionActivities] = useState<ActivityBoardItem[]>([]);

  const selectedRowStartDate = selectedRow?.startDate ? dayjs(selectedRow.startDate) : null;
  const isSelectedPreviousMonthOvertime = isPreviousMonthOvertime(selectedRowStartDate, selectedRow?.isOvertimeWork);

  // form validations & form state
  const schema = yup
    .object({
      activityTypeId: yup
        .mixed()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      effortTrackNo: yup
        .string()
        .trim()
        .when('activityTypeId', {
          is: (value: ActivityType) => value?.isEffortActivity === true,
          then: (schema) =>
            schema.required(
              intl.formatMessage({
                id: 'validations.required',
                defaultMessage: 'Bu alan boş bırakılamaz',
              })
            ),
          otherwise: (schema) => schema.notRequired(),
        })
        .test(
          'is-valid-track-no',
          intl.formatMessage({
            id: 'validations.invalidTrackNo',
            defaultMessage: 'Girdiğiniz efor takip numarası seçili şirket ve ürün ile eşleşmiyor',
          }),
          function (value) {
            if (!value) return true;
            return efforts.some(
              (e) =>
                e.trackNo === value &&
                e.bookId === book?.id &&
                e.projectId === product?.id &&
                e.status !== EFFORT_STATUS_TYPE_ENUM.INVOICED
            );
          }
        ),
      startDate: yup
        .mixed<Dayjs>()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
        .typeError(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
        .test(
          'is-dayjs',
          intl.formatMessage({ id: 'validations.invalidDateFormat', defaultMessage: 'Geçersiz tarih formatı ' }),
          (v) => !v || (dayjs.isDayjs(v) && v.isValid())
        )
        .test(
          'max-3-days-back',
          intl.formatMessage({
            id: 'validations.startDateMaxThreeDaysBack',
            defaultMessage: 'Başlangıç tarihi bugünden en fazla 3 gün geriye dönük olabilir',
          }),
          (value) => {
            if (!value) return true;
            if (isSelectedPreviousMonthOvertime && selectedRowStartDate && value.isSame(selectedRowStartDate, 'day')) {
              return true;
            }

            const threeDaysAgo = dayjs().subtract(3, 'day').startOf('day');
            return value.isAfter(threeDaysAgo) || value.isSame(threeDaysAgo, 'day');
          }
        )
        .test(
          'past-month-overtime-day-unchanged',
          intl.formatMessage({
            id: 'validations.pastMonthOvertimeDateCannotChange',
            defaultMessage: 'Geçen aya ait Fazla Mesai aktivitelerinde gün değiştirilemez',
          }),
          (value) => {
            if (!isSelectedPreviousMonthOvertime || !selectedRowStartDate || !value) return true;

            return value.isSame(selectedRowStartDate, 'day');
          }
        ),
      endDate: yup
        .mixed<Dayjs>()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
        .typeError(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
        .test(
          'is-dayjs',
          intl.formatMessage({ id: 'validations.invalidDateFormat', defaultMessage: 'Geçersiz tarih formatı ' }),
          (v) => !v || (dayjs.isDayjs(v) && v.isValid())
        )
        .test(
          'is-after-start',
          intl.formatMessage({
            id: 'validations.endDateMustBeAfterStartDate',
            defaultMessage: 'Bitiş tarihi başlangıç tarihinden ileri bir tarih olmalıdır',
          }),
          function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;
            return value.isAfter(startDate);
          }
        )
        .test(
          'same-day-as-start',
          intl.formatMessage({
            id: 'validations.endDateMustBeSameDayAsStartDate',
            defaultMessage: 'Bitiş tarihi başlangıç tarihi ile aynı gün olmalıdır',
          }),
          function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;

            return value.isSame(startDate, 'day');
          }
        )
        .test(
          'max-7.5-hours',
          intl.formatMessage({
            id: 'validations.activityMaxEightHours',
            defaultMessage: 'Bir aktivite en fazla 7.5 saat olabilir',
          }),
          function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;

            const diffHours = value.diff(startDate, 'hour', true);
            return diffHours <= 7.5;
          }
        )
        .test(
          'no-crossing-work-boundary',
          intl.formatMessage({
            id: 'validations.cannotCrossWorkHoursBoundary',
            defaultMessage:
              'Aktivite saatleri mesai saatleri sınırını geçemez. Tamamen mesai içinde veya tamamen mesai dışında olmalıdır.',
          }),
          function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;

            // Weekend: no work boundary exists
            const dayOfWeek = startDate.day();
            if (dayOfWeek === 0 || dayOfWeek === 6) return true;

            // Holiday check
            const dateStr = startDate.format('DD.MM.YYYY');
            const holiday = holidays.find((h) => h.localeDateString === dateStr);

            // Full-day holiday: no work boundary exists
            if (holiday && !holiday.isHalfDay) return true;

            // Half-day holiday: work boundary is 09:00-13:00
            const workDayStart = startDate.hour(9).minute(0).second(0).millisecond(0);
            const workDayEnd = holiday?.isHalfDay
              ? startDate.hour(13).minute(0).second(0).millisecond(0)
              : startDate.hour(17).minute(30).second(0).millisecond(0);

            const startInWork =
              (startDate.isAfter(workDayStart) || startDate.isSame(workDayStart)) &&
              (startDate.isBefore(workDayEnd) || startDate.isSame(workDayEnd));
            const endInWork =
              (value.isAfter(workDayStart) || value.isSame(workDayStart)) &&
              (value.isBefore(workDayEnd) || value.isSame(workDayEnd));

            const startOutside =
              startDate.isBefore(workDayStart) ||
              startDate.isSame(workDayStart) ||
              startDate.isAfter(workDayEnd) ||
              startDate.isSame(workDayEnd);
            const endOutside =
              value.isBefore(workDayStart) ||
              value.isSame(workDayStart) ||
              value.isAfter(workDayEnd) ||
              value.isSame(workDayEnd);

            const bothInWork = startInWork && endInWork;
            const bothOutside = startOutside && endOutside;

            return bothInWork || bothOutside;
          }
        ),
      estimatedHours: yup
        .string()
        .trim()
        .notRequired()
        .matches(/^(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/, {
          message: intl.formatMessage({
            id: 'validations.onlyDigits',
            defaultMessage: 'Sadece sayı girilmelidir',
          }),
          excludeEmptyString: true,
        }),
      description: yup
        .string()
        .trim()
        .max(
          500,
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 500 }
          )
        )
        .notRequired(),
      isOvertimeWork: yup.boolean().test('overtime-work-hours-check', '', function (value) {
        const { startDate, endDate } = this.parent;
        if (!startDate || !endDate) return true;

        if (isSelectedPreviousMonthOvertime) {
          if (value !== true) {
            return this.createError({
              message: intl.formatMessage({
                id: 'validations.pastMonthOvertimeMustRemainOvertime',
                defaultMessage: 'Geçen aya ait Fazla Mesai aktivitelerinde Fazla Mesai seçimi kaldırılamaz',
              }),
            });
          }

          if (selectedRowStartDate && !startDate.isSame(selectedRowStartDate, 'day')) {
            return this.createError({
              message: intl.formatMessage({
                id: 'validations.pastMonthOvertimeDateCannotChange',
                defaultMessage: 'Geçen aya ait Fazla Mesai aktivitelerinde gün değiştirilemez',
              }),
            });
          }

          const originalHoursFromDates =
            selectedRow?.startDate && selectedRow.endDate
              ? dayjs(selectedRow.endDate).diff(dayjs(selectedRow.startDate), 'hour', true)
              : 0;
          const originalHours = Number(selectedRow?.estimatedHours) || originalHoursFromDates;
          const updatedHours = endDate.diff(startDate, 'hour', true);

          if (updatedHours - originalHours > 0.0001) {
            return this.createError({
              message: intl.formatMessage({
                id: 'validations.pastMonthOvertimeHoursCannotIncrease',
                defaultMessage: 'Geçen aya ait Fazla Mesai aktivitelerinde süre artırılamaz',
              }),
            });
          }
        } else if (isPastMonthOvertime(startDate, value)) {
          return this.createError({
            message: intl.formatMessage({
              id: 'validations.overtimeNotAllowedForPastMonths',
              defaultMessage: 'Geçmiş aya ait aktivitelerde Fazla Mesai seçilemez',
            }),
          });
        }

        const dayOfWeek = startDate.day();
        const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;

        // Holiday check
        const dateStr = startDate.format('DD.MM.YYYY');
        const holiday = holidays.find((h) => h.localeDateString === dateStr);

        // Weekend: overtime must always be selected
        if (isWeekendDay) {
          if (value === false) {
            return this.createError({
              message: intl.formatMessage({
                id: 'validations.overtimeRequiredOnWeekend',
                defaultMessage: 'Hafta sonu aktiviteleri için Fazla Mesai seçilmelidir',
              }),
            });
          }
          return true;
        }

        // Full-day holiday: overtime must always be selected
        if (holiday && !holiday.isHalfDay) {
          if (value === false) {
            return this.createError({
              message: intl.formatMessage({
                id: 'validations.overtimeRequiredOnHoliday',
                defaultMessage: 'Resmi tatil günlerinde Fazla Mesai seçilmelidir',
              }),
            });
          }
          return true;
        }

        // Half-day holiday: 09:00-13:00 is normal work, outside is overtime
        if (holiday && holiday.isHalfDay) {
          const halfDayWorkStart = startDate.hour(9).minute(0).second(0).millisecond(0);
          const halfDayWorkEnd = startDate.hour(13).minute(0).second(0).millisecond(0);

          const isWithinHalfDayWork =
            (startDate.isAfter(halfDayWorkStart) || startDate.isSame(halfDayWorkStart)) &&
            (endDate.isBefore(halfDayWorkEnd) || endDate.isSame(halfDayWorkEnd));

          if (isWithinHalfDayWork && value === true) {
            const hasPermOverlap = userPermissionActivities.some((pa) => {
              if (!pa.startDate || !pa.endDate) return false;
              const paStart = dayjs(pa.startDate);
              const paEnd = dayjs(pa.endDate);
              return startDate.isBefore(paEnd) && endDate.isAfter(paStart);
            });
            if (!hasPermOverlap) {
              return this.createError({
                message: intl.formatMessage({
                  id: 'validations.overtimeNotAllowedInHalfDayWorkHours',
                  defaultMessage: 'Yarım gün tatil mesai saatleri (09:00-13:00) içinde Fazla Mesai seçilemez',
                }),
              });
            }
          }

          if (!isWithinHalfDayWork && value === false) {
            return this.createError({
              message: intl.formatMessage({
                id: 'validations.overtimeRequiredOnHoliday',
                defaultMessage: 'Resmi tatil günlerinde Fazla Mesai seçilmelidir',
              }),
            });
          }

          return true;
        }

        // Regular weekday: existing logic
        const workDayStart = startDate.hour(9).minute(0).second(0).millisecond(0);
        const workDayEnd = startDate.hour(17).minute(30).second(0).millisecond(0);

        const isWithinWorkHours =
          (startDate.isAfter(workDayStart) || startDate.isSame(workDayStart)) &&
          (endDate.isBefore(workDayEnd) || endDate.isSame(workDayEnd));

        if (isWithinWorkHours && value === true) {
          const hasPermOverlap = userPermissionActivities.some((pa) => {
            if (!pa.startDate || !pa.endDate) return false;
            const paStart = dayjs(pa.startDate);
            const paEnd = dayjs(pa.endDate);
            return startDate.isBefore(paEnd) && endDate.isAfter(paStart);
          });
          if (!hasPermOverlap) {
            return this.createError({
              message: intl.formatMessage({
                id: 'validations.overtimeNotAllowedInWorkHours',
                defaultMessage: 'Mesai saatleri içinde Fazla Mesai seçilemez',
              }),
            });
          }
        }

        if (!isWithinWorkHours && value === false) {
          return this.createError({
            message: intl.formatMessage({
              id: 'validations.overtimeRequiredOutsideWorkHours',
              defaultMessage: 'Mesai saatleri dışındaki saatlerde Fazla Mesai seçilmelidir',
            }),
          });
        }

        return true;
      }),
    })
    .required();

  const { reset, control, watch, setValue, handleSubmit } = useForm<UpdateActivityBoardItemFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');

  const filteredEfforts = useMemo(
    () =>
      efforts.filter(
        (e) => e.bookId === book?.id && e.projectId === product?.id && e.status !== EFFORT_STATUS_TYPE_ENUM.INVOICED
      ),
    [efforts, book, product]
  );

  const filteredActivityTypes = useMemo(
    () => activityTypes.filter((activityType) => !activityType.isPermissionActivity),
    [activityTypes]
  );

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setBook(null);
    setProduct(null);
    reset(defaultValues);
  };

  const fetchEfforts = useCallback(async () => {
    try {
      const resp = await apiService.getEfforts({ statusType: null, appointedConsultantId: null });
      if (resp.isSuccess && resp.data) {
        setEfforts(resp.data);
      }
    } catch {
      setEfforts([]);
    }
  }, []);

  const fetchHolidays = useCallback(async (year: number) => {
    try {
      const resp = await apiService.getHolidays(year);
      if (resp.isSuccess && resp.data) {
        setHolidays(resp.data);
      }
    } catch {
      setHolidays([]);
    }
  }, []);

  const fetchUserPermissionActivities = useCallback(async () => {
    try {
      const me = users.find((u) => u.nameSurname === Authentication.getLoggedUser());
      if (!me) return;
      const resp = await apiService.getActivityBoardItems({
        bookId: null,
        projectId: null,
        userId: me.id,
        startDate: null,
        endDate: null,
      });
      if (resp.isSuccess) {
        const permActivities = resp.data.filter((a) => {
          if (a.id === selectedRow?.id) return false;
          const aType = activityTypes.find((at) => at.id === a.activityTypeId);
          return aType?.isPermissionActivity === true;
        });
        setUserPermissionActivities(permActivities);
      }
    } catch {
      setUserPermissionActivities([]);
    }
  }, [users, activityTypes, selectedRow]);

  // async helper functions
  const onSubmit = async (data: UpdateActivityBoardItemFormParams) => {
    if (!selectedRow || !data.activityTypeId) return;

    const postData: UpdateActivityBoardItemPostFormParams = {
      id: selectedRow.id,
      bookId: selectedRow.bookId,
      projectId: selectedRow.projectId,
      activityTypeId: data.activityTypeId.id,
      effortTrackNo: data.effortTrackNo,
      startDate: data.startDate ? data.startDate.toISOString() : null,
      endDate: data.endDate ? data.endDate.toISOString() : null,
      estimatedHours: Number(data.estimatedHours) || 0,
      description: data.description,
      isOvertimeWork: data.isOvertimeWork,
    };

    try {
      setLoading(true);

      // Permission activity check: if user has permission activities overlapping, overtime is required
      if (data.startDate && data.endDate) {
        const newStart = data.startDate;
        const newEnd = data.endDate;

        const hasOverlap = userPermissionActivities.some((pa) => {
          if (!pa.startDate || !pa.endDate) return false;
          const paStart = dayjs(pa.startDate);
          const paEnd = dayjs(pa.endDate);
          return newStart.isBefore(paEnd) && newEnd.isAfter(paStart);
        });

        if (hasOverlap && !data.isOvertimeWork) {
          enqueueSnackbar(
            intl.formatMessage({
              id: 'validations.overtimeRequiredDuringPermission',
              defaultMessage:
                'Seçilen saat aralığında izinli olduğunuz için bu zaman diliminde yalnızca Fazla Mesai seçerek aktivite girebilirsiniz.',
            }),
            { variant: 'warning' }
          );
          setLoading(false);
          return;
        }
      }

      // Daily 7.5-hour regular work limit validation (non-overtime)
      if (data.startDate && !data.isOvertimeWork) {
        const me = users.find((u) => u.nameSurname === Authentication.getLoggedUser());
        if (me) {
          const activitiesResp = await apiService.getActivityBoardItems({
            bookId: null,
            projectId: null,
            userId: me.id,
            startDate: data.startDate.startOf('day').toISOString(),
            endDate: data.startDate.endOf('day').toISOString(),
          });

          if (activitiesResp.isSuccess) {
            const existingRegularHours = activitiesResp.data
              .filter((a) => !a.isOvertimeWork && a.id !== selectedRow!.id)
              .reduce((sum, a) => sum + (a.estimatedHours || 0), 0);

            const newHours = Number(data.estimatedHours) || 0;

            if (existingRegularHours + newHours > 7.5) {
              enqueueSnackbar(
                intl.formatMessage(
                  {
                    id: 'validations.dailyRegularHoursExceeded',
                    defaultMessage:
                      'Bu gün için normal mesai çalışma süresi toplamı ({totalHours} saat) 7.5 saati aşmaktadır. Mevcut giriş: {existingHours} saat.',
                  },
                  {
                    totalHours: (existingRegularHours + newHours).toFixed(2),
                    existingHours: existingRegularHours.toFixed(2),
                  }
                ),
                { variant: 'warning' }
              );
              setLoading(false);
              return;
            }
          }
        }
      }

      // Effort track no hours limit validation
      if (data.effortTrackNo && data.activityTypeId.isEffortActivity) {
        const matchingEffort = efforts.find((effort) => effort.trackNo === data.effortTrackNo);

        if (matchingEffort) {
          const maxHours = matchingEffort.effortDay * 7.5;

          const activitiesResp = await apiService.getActivityBoardItems({
            bookId: selectedRow.bookId,
            projectId: selectedRow.projectId,
            userId: null,
            startDate: null,
            endDate: null,
          });

          if (activitiesResp.isSuccess) {
            const effortActivityTypeIds = activityTypes.filter((at) => at.isEffortActivity).map((at) => at.id);

            const existingHours = activitiesResp.data
              .filter(
                (activity) =>
                  activity.effortTrackNo === data.effortTrackNo &&
                  effortActivityTypeIds.includes(activity.activityTypeId) &&
                  activity.id !== selectedRow!.id
              )
              .reduce((sum, activity) => sum + (activity.estimatedHours || 0), 0);

            const newHours = Number(data.estimatedHours) || 0;

            if (existingHours + newHours > maxHours) {
              enqueueSnackbar(
                intl.formatMessage(
                  {
                    id: 'validations.effortDayHoursExceeded',
                    defaultMessage:
                      'Bu efor takip numarasına ait toplam çalışma süresi ({totalHours} saat), efor gün limitini ({maxHours} saat = {effortDay} gün x 7.5 saat) aşmaktadır.',
                  },
                  {
                    totalHours: (existingHours + newHours).toFixed(2),
                    maxHours: String(maxHours),
                    effortDay: String(matchingEffort.effortDay),
                  }
                ),
                { variant: 'warning' }
              );

              setLoading(false);
              return;
            }
          }
        }
      }

      const response = await apiService.updateActivityBoardItem(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="activityManaging.updateActivityBoardItemCompleted"
            defaultMessage="Aktivite güncelleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const populateForm = useCallback(() => {
    if (!selectedRow) return;

    const foundBook = books.find((b) => b.id === selectedRow.bookId);
    if (foundBook) setBook(foundBook);

    const foundProduct = products.find((p) => p.id === selectedRow.projectId);
    if (foundProduct) setProduct(foundProduct);

    const foundActivityType = activityTypes.find((at) => at.id === selectedRow.activityTypeId);
    if (foundActivityType) setValue('activityTypeId', foundActivityType);

    setValue('effortTrackNo', selectedRow.effortTrackNo ?? '');
    setValue('startDate', selectedRow.startDate ? dayjs(selectedRow.startDate) : null);
    setValue('endDate', selectedRow.endDate ? dayjs(selectedRow.endDate) : null);
    setValue('estimatedHours', selectedRow.estimatedHours ? String(selectedRow.estimatedHours) : '');
    setValue('description', selectedRow.description ?? '');
    setValue('isOvertimeWork', selectedRow.isOvertimeWork ?? false);
  }, [selectedRow, books, products, activityTypes, setValue]);

  // useEffect hooks
  useEffect(() => {
    if (open && selectedRow) {
      populateForm();
      fetchEfforts();
    }
  }, [open, selectedRow, populateForm, fetchEfforts]);

  useEffect(() => {
    if (watchStartDate && watchEndDate && watchEndDate.isAfter(watchStartDate)) {
      const diffHours = watchEndDate.diff(watchStartDate, 'hour', true);
      setValue('estimatedHours', diffHours.toFixed(2));
    } else {
      setValue('estimatedHours', '0');
    }
  }, [watchStartDate, watchEndDate, setValue]);

  const startDateYear = watchStartDate?.year();
  useEffect(() => {
    if (startDateYear) {
      fetchHolidays(startDateYear);
    }
  }, [startDateYear, fetchHolidays]);

  useEffect(() => {
    if (open && selectedRow) {
      fetchUserPermissionActivities();
    } else {
      setUserPermissionActivities([]);
    }
  }, [open, selectedRow, fetchUserPermissionActivities]);

  return (
    <ResponsiveDialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="activityManaging.updateActivityBoardItem" defaultMessage="Aktivite Güncelle" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            <FormattedMessage
              id="activityManaging.updateActivityBoardItemText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili aktivite için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="bookId">
                <FormattedMessage id="effortTracing.bookId" defaultMessage="Proje Seçimi" />
              </FormLabel>
              <TextField fullWidth size="small" value={book?.companyName ?? ''} disabled />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="projectId">
                <FormattedMessage id="effortTracing.projectId" defaultMessage="İlgili Ürün" />
              </FormLabel>
              <TextField fullWidth size="small" value={product?.title ?? ''} disabled />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="activityTypeId">
                <FormattedMessage id="activityManaging.activityType" defaultMessage="Aktivite Türü" />
              </FormLabel>
              <Controller
                name="activityTypeId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    size="small"
                    options={filteredActivityTypes}
                    value={value}
                    {...renderAutoCompleteLocaleText(intl)}
                    onChange={(_, newValue: ActivityType | null) => onChange(newValue)}
                    getOptionLabel={(option: ActivityType) => option.title}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="effortTrackNo">
                <FormattedMessage id="activityManaging.effortTrackNo" defaultMessage="Efor Takip No" />
              </FormLabel>
              <Controller
                name="effortTrackNo"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    disabled={!book || !product}
                    freeSolo
                    size="small"
                    options={filteredEfforts.map((e) => e.trackNo)}
                    value={field.value || null}
                    onChange={(_, newValue) => field.onChange((newValue as string | null) ?? '')}
                    onInputChange={(_, newInputValue) => field.onChange(newInputValue)}
                    {...renderAutoCompleteLocaleText(intl)}
                    slotProps={{
                      paper: {
                        sx: {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900],
                          minWidth: { xs: 0, sm: 360 },
                          maxWidth: 'calc(100vw - 32px)',
                        },
                      },
                    }}
                    renderOption={(props, option) => {
                      const effort = filteredEfforts.find((e) => e.trackNo === option);
                      const consultant = effort ? users.find((u) => u.id === effort.appointedConsultantId) : null;
                      return (
                        <li {...props} key={option}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 0.25 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {option}
                            </Typography>
                            {effort && (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  {intl.formatMessage({
                                    id: 'effortTracing.customerFullName',
                                    defaultMessage: 'Talep Eden Müşteri Ad Soyad',
                                  })}
                                  : {effort.customerFullName || '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {intl.formatMessage({
                                    id: 'effortTracing.explanation',
                                    defaultMessage: 'Efor Açıklaması',
                                  })}
                                  : {effort.explanation || '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {intl.formatMessage({ id: 'effortTracing.effortDay', defaultMessage: 'Efor Günü' })}:{' '}
                                  {effort.effortDay}
                                  {consultant &&
                                    ` · ${intl.formatMessage({ id: 'effortTracing.appointedConsultantEmail', defaultMessage: 'Atanan Danışman' })}: ${consultant.nameSurname}`}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="startDate">
                <FormattedMessage id="activityManaging.startDate" defaultMessage="Başlangıç Tarihi" />
              </FormLabel>
              <Controller
                name="startDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    {...field}
                    ampm={false}
                    localeText={renderDatePickerLocaleText(intl)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="endDate">
                <FormattedMessage id="activityManaging.endDate" defaultMessage="Bitiş Tarihi" />
              </FormLabel>
              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    {...field}
                    ampm={false}
                    localeText={renderDatePickerLocaleText(intl)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="estimatedHours">
                <FormattedMessage id="activityManaging.estimatedHours" defaultMessage="Harcanan Süre (saat)" />
              </FormLabel>
              <Controller
                name="estimatedHours"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} disabled />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} justifyItems={'center'}>
              <Box>
                <FormLabel htmlFor="isOvertimeWork">
                  <FormattedMessage id="activityManaging.isOvertimeWork" defaultMessage="Fazla Mesai" />
                </FormLabel>
              </Box>
              <Box>
                <Controller
                  name="isOvertimeWork"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <FormControlLabel
                        control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                        label={
                          field.value
                            ? intl.formatMessage({ id: 'common.yes', defaultMessage: 'Evet' })
                            : intl.formatMessage({ id: 'common.no', defaultMessage: 'Hayır' })
                        }
                      />
                      <FormHelperText error={!!error}>{error?.message}</FormHelperText>
                    </Box>
                  )}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormLabel htmlFor="description">
                <FormattedMessage id="activityManaging.description" defaultMessage="Açıklama" />
              </FormLabel>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={4}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default UpdateActivityBoardItemDialog;
